// Echte Portfolio-Wertreihe (EUR-Basis) der letzten N Tage — ersetzt den
// früheren synthetischen Verlaufschart im Dashboard.
//
// Drei Schritte:
//   1. Bestands-Rekonstruktion pro Coin pro Tag: signierte Laufbilanz aus den
//      Transaktionen (dieselbe Logik wie debugBalanceIssues in portfolioCalc.js —
//      OHNE FIFO/Lots, denn für die reine gehaltene MENGE an einem Stichtag
//      brauchen wir keine Kostenbasis, nur Σ Eingänge − Σ Ausgänge bis zum Tag).
//   2. Historische Tagespreise pro Coin via Proxy
//      `/api/coin-price-history?days=…` (CoinGecko market_chart, ein Call pro
//      Coin, mit localStorage-Cache → schont Rate-Limits).
//   3. Pro Tag: Σ Menge(coin, tag) × Preis(coin, tag).
//
// Fiat/NFT werden wie im restlichen Portfolio ausgeschlossen (PORTFOLIO_FIAT_EXCLUDE).

import { COIN_MAP } from './coingecko';
import { safeLocalSet } from './lsCache';

const FIAT_EXCLUDE = new Set([
  'EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL', 'NFT',
]);
const DAY_MS = 86400000;
const CACHE_PREFIX = 'red_pxhist_';
// 24 h — die Tageshistorie ist unveränderlich; nur der jüngste Punkt bewegt sich,
// und der angezeigte Live-Wert kommt aus marketData (nicht aus dieser Reihe). Längerer
// Cache spart massiv CoinGecko-Calls (market_chart pro Coin, nicht batchbar).
const CACHE_TTL_MS = 24 * 3600 * 1000;

// Lokales Kalender-Tagesdatum "YYYY-MM-DD" aus einem ms-Timestamp.
function toLocalDayStr(ms) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Aufsteigende Liste der letzten n Kalendertage (lokal), endend mit heute.
export function lastNDays(n) {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const out = [];
  for (let i = n - 1; i >= 0; i--) out.push(toLocalDayStr(base - i * DAY_MS));
  return out;
}

function txDay(tx) {
  return (tx.effectiveTimestamp ? String(tx.effectiveTimestamp).slice(0, 10) : '')
    || String(tx.datum || '').slice(0, 10);
}

/**
 * Tägliche gehaltene Menge pro Coin, ausgerichtet an `dayList`.
 * @returns {{ [sym: string]: number[] }} — Array gleicher Länge wie dayList.
 */
export function buildDailyBalances(txs, dayList) {
  // coin → Map(dayStr → kumulierter Tages-Delta)
  const deltas = {};
  const add = (coin, amt, day) => {
    if (!coin || !amt || !day) return;
    const sym = String(coin).toUpperCase();
    if (FIAT_EXCLUDE.has(sym)) return;
    if (!deltas[sym]) deltas[sym] = new Map();
    deltas[sym].set(day, (deltas[sym].get(day) || 0) + amt);
  };

  for (const tx of txs || []) {
    const day = txDay(tx);
    if (!day) continue;
    const m = Number(tx.menge) || 0;
    switch (tx.typ) {
      case 'kauf':
      case 'einzahlung':
      case 'staking':
      case 'airdrop':
      case 'mining':
      case 'cashback':
      case 'interest':
      case 'income':
        add(tx.coin ?? tx.sym, m, day);
        break;
      case 'verkauf':
      case 'auszahlung':
      case 'burn':
      case 'loss':
        add(tx.coin ?? tx.sym, -m, day);
        break;
      case 'swap':
        add(tx.coinOut ?? tx.symOut, -(Number(tx.mengeOut) || 0), day);
        add(tx.coinIn ?? tx.symIn, (Number(tx.mengeIn) || 0), day);
        break;
      // futures_pnl / funding_fee: vorzeichenbehaftetes `preis` bestimmt die Richtung
      case 'futures_pnl':
      case 'funding_fee':
        if ((tx.coin || tx.sym) && m > 0) {
          if (tx.preis < 0) add(tx.coin ?? tx.sym, -m, day);
          else if (tx.preis > 0) add(tx.coin ?? tx.sym, m, day);
        }
        break;
      default:
        break;
    }
  }

  const result = {};
  for (const [sym, dmap] of Object.entries(deltas)) {
    const evDays = [...dmap.keys()].sort(); // lexikografisch = chronologisch für YYYY-MM-DD
    const series = new Array(dayList.length);
    let cum = 0;
    let pi = 0;
    for (let i = 0; i < dayList.length; i++) {
      const target = dayList[i];
      // Alle Deltas mit Event-Tag ≤ Stichtag aufaddieren (inkl. Deltas VOR dem Fenster).
      while (pi < evDays.length && evDays[pi] <= target) {
        cum += dmap.get(evDays[pi]);
        pi++;
      }
      // Mikro-Negative durch Float-Rauschen vermeiden.
      series[i] = cum > 1e-12 ? cum : 0;
    }
    result[sym] = series;
  }
  return result;
}

// Preis eines Coins am nächstgelegenen Zeitpunkt (binäre Suche über sortierte Punkte).
function nearestPrice(points, ms) {
  if (!points || !points.length) return null;
  if (ms <= points[0].ms) return points[0].price;
  if (ms >= points[points.length - 1].ms) return points[points.length - 1].price;
  let lo = 0, hi = points.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid].ms === ms) return points[mid].price;
    if (points[mid].ms < ms) lo = mid + 1; else hi = mid - 1;
  }
  const before = points[hi];
  const after = points[lo];
  if (!before) return after.price;
  if (!after) return before.price;
  return (ms - before.ms) <= (after.ms - ms) ? before.price : after.price;
}

// Preisreihe eines Coins holen (sortierte [{ms, price}], localStorage-Cache).
// `days` steuert die Granularität (CoinGecko: 1=5-Min, 2–90=stündlich, >90=täglich).
async function fetchCoinPricePoints(coinId, currency, days) {
  const cacheKey = `${CACHE_PREFIX}${coinId}_${currency}_${days}`;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && Array.isArray(obj.points) && Date.now() - (obj.fetchedAt || 0) < CACHE_TTL_MS) {
        return obj.points;
      }
    }
  } catch { /* localStorage nicht verfügbar / korrupt → neu holen */ }

  try {
    const _base = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() ? 'https://app.redcrypto.de' : '';
    const url = `${_base}/api/coin-price-history?coin=${encodeURIComponent(coinId)}&currency=${currency}&days=${days}`;
    const r = await fetch(url, { signal: AbortSignal.timeout(12000) });
    if (!r.ok) return null;
    const json = await r.json();
    const prices = json?.prices;
    if (!Array.isArray(prices)) return null;
    const points = [];
    for (const entry of prices) {
      const ms = entry?.[0];
      const px = entry?.[1];
      if (typeof ms === 'number' && typeof px === 'number' && Number.isFinite(px)) {
        points.push({ ms, price: px });
      }
    }
    points.sort((a, b) => a.ms - b.ms);
    // safeLocalSet: Budget-begrenzt, verdrängt bei Quota ältere Preis-Caches (verhindert
    // localStorage-Überlauf → Firestore QuotaExceededError → App-Crash).
    safeLocalSet(cacheKey, JSON.stringify({ fetchedAt: Date.now(), points }));
    return points;
  } catch {
    return null;
  }
}

/**
 * Baut die echte Portfolio-Wertreihe in der Granularität von `days`.
 * Die Zeitachse stammt aus den (Intraday- oder Tages-)Preispunkten von CoinGecko;
 * die gehaltene Menge pro Coin ist innerhalb eines Kalendertages konstant (TX-Daten
 * sind tagesgenau), wird also je Achsenpunkt über dessen Kalendertag nachgeschlagen.
 *
 * @returns {Promise<{ values: number[], ts: number[], ok: boolean, missing: string[] }>}
 *   values/ts (ms-Timestamps) sind ausgerichtet und um führende Null-Punkte
 *   (vor Portfolio-Start) gekürzt. `missing` = Coins ohne CoinGecko-ID/Preisreihe.
 */
export async function buildPortfolioValueSeries({ txs, days = 365, currency = 'eur', windowDays } = {}) {
  // 1. Gehaltene Coins im Fenster bestimmen (grobe Tagesbilanz reicht dafür).
  const coarseDays = lastNDays(days < 1 ? 1 : days);
  const coarseBal = buildDailyBalances(txs, coarseDays);
  const heldSyms = Object.keys(coarseBal).filter(sym => coarseBal[sym].some(v => v > 0));
  const priceable = heldSyms.filter(sym => COIN_MAP[sym]?.id);
  const missing = heldSyms.filter(sym => !COIN_MAP[sym]?.id);

  // 2. Preispunkte je Coin holen (kleine Parallelität gegen Rate-Limits).
  // Kalter Burst (alle Coins gleichzeitig) überrennt sonst das keyless-CoinGecko-
  // Rate-Limit → 429, nichts wird gecacht, jeder Reload feuert erneut. Niedrige
  // Concurrency + kurzer Stagger zwischen den Chunks hält die Calls unters Limit,
  // sodass der 12-h-Edge-Cache (s-maxage) warmläuft. Warme Coins kommen aus dem
  // Edge-Cache (kein CoinGecko-Call) → Stagger kostet dann nichts.
  const pointsMap = {}; // sym → [{ms, price}]
  const CHUNK = 2;
  const STAGGER_MS = 300;
  for (let i = 0; i < priceable.length; i += CHUNK) {
    const chunk = priceable.slice(i, i + CHUNK);
    const got = await Promise.all(
      chunk.map(sym => fetchCoinPricePoints(COIN_MAP[sym].id, currency, days).then(p => [sym, p]))
    );
    for (const [sym, p] of got) {
      if (p && p.length) pointsMap[sym] = p;
      else missing.push(sym);
    }
    if (i + CHUNK < priceable.length) {
      await new Promise(r => setTimeout(r, STAGGER_MS));
    }
  }
  const valued = priceable.filter(sym => pointsMap[sym]);
  if (!valued.length) return { values: [], ts: [], ok: false, missing: [...new Set(missing)] };

  // 3. Referenz-Zeitachse = längste Coin-Reihe (feinste Auflösung).
  let axis = pointsMap[valued[0]];
  for (const sym of valued) if (pointsMap[sym].length > axis.length) axis = pointsMap[sym];
  const ts = axis.map(p => p.ms);

  // 4. Tagesbilanz auf den im Achsenfenster vorkommenden Kalendertagen.
  const axisDayStrs = [...new Set(ts.map(toLocalDayStr))].sort();
  const fineBal = buildDailyBalances(txs, axisDayStrs);
  const dayIdx = new Map(axisDayStrs.map((d, i) => [d, i]));

  // 5. Wert je Achsenpunkt: Σ Menge(coin, tag(ms)) × Preis(coin, ms).
  const values = ts.map(ms => {
    const di = dayIdx.get(toLocalDayStr(ms));
    let v = 0;
    for (const sym of valued) {
      const bal = fineBal[sym]?.[di];
      if (!bal) continue;
      const px = nearestPrice(pointsMap[sym], ms);
      if (px != null) v += bal * px;
    }
    return v;
  });

  // 6. Führende Null-Punkte (Portfolio existierte noch nicht) abschneiden.
  let start = 0;
  while (start < values.length - 1 && values[start] <= 0) start++;

  // 7. Optional auf das angezeigte Fenster (windowDays) begrenzen. Erlaubt, EINE
  //    längere Reihe (Bucket, z.B. 90 Tage) zu holen und für 7T/30T/90T nur
  //    clientseitig zu schneiden → keine separaten CoinGecko-Calls je Range.
  if (Number.isFinite(windowDays) && windowDays > 0 && ts.length) {
    const cutoff = ts[ts.length - 1] - windowDays * DAY_MS;
    let ws = 0;
    while (ws < ts.length - 1 && ts[ws] < cutoff) ws++;
    if (ws > start) start = ws;
  }

  return {
    values: values.slice(start),
    ts: ts.slice(start),
    ok: values.length - start >= 2,
    missing: [...new Set(missing)],
  };
}
